import React, { useEffect, useState, useRef, useContext } from "react";
import { Box, Typography, List, ListItem, ListItemText, Avatar } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startStream } from "./../../action/stream";
import { addSongAction } from "./../../action/songs";
import ApiService from "./../../services/api-service";
import { truncate, isOwnerId } from "./../../constants/function";
import Music from "./../../assets/common/resources/default/Music.svg";
import { keyframes } from "@mui/system";
import { SocketContext } from "../../context/socket";
import cookieStorage from "../../services/cookie-storage";

const SidebarRight = () => {
  const socket = useContext(SocketContext).Socket;
  const [topSongs, setTopSongs] = useState([]);          // Initialize as empty array
  const [liveStreams, setLiveStreams] = useState([]);    // Initialize as empty array
  const [topPlaylists, setTopPlaylists] = useState([]);  // Initialize as empty array
  const [featuredArtist, setFeaturedArtist] = useState(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const streamRef = useRef([]);

  const pulseAnimation = keyframes`
    0% { transform: scale(1); opacity: 0.5; background-color: darkred; }
    50% { transform: scale(1.3); opacity: 1; background-color: red; }
    100% { transform: scale(1); opacity: 0.5; background-color: darkred; }
  `;

  useEffect(() => {
    getNewReleaseSongs();
    getLiveStreams();
    mostStreamedPlaylist();
    getFeaturedArtist();


    return () => {
      socket.off("stream-joined", handleStreamJoined);
      socket.off("stream-ended", handleStreamEnded);
    };
  }, [socket]);

  const getNewReleaseSongs = async () => {
    try {
      const response = await ApiService.MUSIC_NEW_RELEASE();
      if (response?.result?.length) {
        setTopSongs(response.result.slice(0, 4));
      } else {
        setTopSongs([]);
      }
    } catch (error) {
      console.error("Error fetching new release songs:", error);
      setTopSongs([]);
    }
  };

  const mostStreamedPlaylist = async () => {
    try {
      const response = await ApiService.PLAYLIST_STREAMED_LASTWEEK();
      const playlists = response?.result ? response.result.slice(0, 4) : [];
      setTopPlaylists(playlists);
    } catch (error) {
      console.error("Error fetching playlists:", error);
      setTopPlaylists([]);
    }
  };

  const getLiveStreams = async () => {
    try {
      const response = await ApiService.LIVESTREAM();
      console.log("Live Streams Response:", response);
      const streams = response?.result
        ? response.result.slice(0, 4).map((stream) => ({
            id: stream.channelName,
            title: stream.title || "Live Stream",
            creatorId: stream.createdById || "",
            creator: `${stream.createdByDetails.firstName || ""} ${stream.createdByDetails.lastName || ""}`.trim(),
            thumbnail: stream.GIF?.filePath || stream.thumbnailImg?.filePath || Music,
          }))
        : [];
      console.log("Live Streams:", streams);
      setLiveStreams(streams);
      streamRef.current = streams;
    } catch (error) {
      console.error("Error fetching live streams:", error);
      setLiveStreams([]);
    }
  };

  const getFeaturedArtist = async () => {
    try {
      const response = await ApiService.FEATURE_ARTIST();
      if (response?.result && response.result.length > 0) {
        setFeaturedArtist(response.result[0]);
      } else {
        setFeaturedArtist(null);
      }
    } catch (error) {
      console.error("Error fetching featured artist:", error);
      setFeaturedArtist(null);
    }
  };

  const handleStreamJoined = (streamData) => {
    if (!streamData || !streamData.livestreamData) return;
    const newStream = streamData.livestreamData;
    console.log("streamData:", streamData);
    if (!streamRef.current.some((stream) => stream.id === newStream.channelName)) {
      const updatedStreams = [
        {
          id: newStream.channelName,
          title: newStream.title || "Live Stream",
          creatorId: newStream.createdById || "",
          creator: newStream.createdBy || "Unknown Creator",
          thumbnail: newStream.GIF?.filePath || newStream.thumbnailImg?.filePath || Music,
        },
        ...streamRef.current,
      ];
      setLiveStreams(updatedStreams);
      streamRef.current = updatedStreams;
    }
  };

  const handleStreamEnded = (data) => {
    const updatedStreams = streamRef.current.filter(stream => stream.id !== data.channelName);
    setLiveStreams(updatedStreams);
    streamRef.current = updatedStreams;
  };

  const handlePlayStream = (channelName) => {
    ApiService.PLAYLIST_DETAIL({ _id: channelName, pageNo: 1, pageSize: 1 })
      .then((response) => {
        if (response?.result?.live) {
          const userToken = cookieStorage.getCookie("Authorization");
          const isOwner = isOwnerId(response?.result?.nftOwner?.owner);

          const payload = {
            channelName: response.result._id,
            role: isOwner ? "host" : "audience",
            JWT: userToken,
            platform: "web",
          };

          if (isOwner) {
            payload.goalAmount = response.result.goalAmount;
            payload.goalTitle = response.result.title;
            payload.goalDescription = response.result.goalDescription;
            payload.isNew = response.result.isNew;
          }

          socket.emit("stream-start", payload);

          dispatch(startStream({ streamData: response.result }));
          history.push(`/app/live/${channelName}`);
        }
      })
      .catch((error) => {
        console.error("Error joining live stream:", error);
      });
  };

  const handlePlaySong = (musicItem) => {
    dispatch(addSongAction(musicItem));
  };

  return (
    <Box
      sx={{
        backgroundColor: "black",
        minHeight: "100vh",
        width: "100%",
        padding: "15px 15px 50px 15px",
        color: "white",
        marginTop: "46px",
        overflowY: "auto",
        height: "calc(100vh - 46px)",
        borderRadius: "16px",
      }}
    >
      {/* NOW LIVE */}
      {liveStreams.length > 0 && (
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "12px", color: "#ffffff" }}>
              NOW LIVE
            </Typography>
            <Typography
              sx={{
                color: "#ffffff",
                fontSize: "9px",
                textTransform: "none",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => history.push('/app/live')}
            >
              Explore More
            </Typography>
          </Box>
          <List>
            {liveStreams.map((stream) => (
              <ListItem
                key={stream.id}
                sx={{
                  pl: 1.5,
                  py: 0.8,
                  borderRadius: "8px",
                  backgroundColor: "rgba(13, 13, 13, 0.9)",
                  border: "1px solid transparent",
                  background: `linear-gradient(black, black) padding-box, 
                               linear-gradient(135deg, #1f1f2e, #41416e) border-box`,
                  boxShadow: "none",
                  mb: 1,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: "0px 0px 8px rgba(65, 65, 110, 0.6)",
                  },
                }}
              >
                <Avatar
                src={stream?.thumbnail || stream?.thumbnailImg}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "6px",
                    mr: 1.5,
                    cursor: "pointer",
                  }}
                  onClick={() => handlePlayStream(stream.id)}
                />
                <ListItemText
                  primary={truncate(stream.title, 12)}
                  secondary={stream.creator}
                  primaryTypographyProps={{ fontSize: "12px", fontWeight: "bold", color: "#ffffff" }}
                  secondaryTypographyProps={{ color: "gray", fontSize: "10px" }}
                  onClick={() => handlePlayStream(stream.id)}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* NEW RELEASES */}
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "12px", color: "#ffffff" }}>
            NEW RELEASES
          </Typography>
          <Typography
            sx={{
              color: "#ffffff",
              fontSize: "9px",
              textTransform: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => history.push('/app/feature/NewRelease')}
          >
            Explore More
          </Typography>
        </Box>
        <List>
          {topSongs.map((song) => (
            <ListItem
              key={song._id}
              sx={{
                pl: 1.5,
                py: 0.8,
                borderRadius: "8px",
                backgroundColor: "rgba(13, 13, 13, 0.9)",
                border: "1px solid transparent",
                background: `linear-gradient(black, black) padding-box, 
                             linear-gradient(135deg, #1f1f2e, #41416e) border-box`,
                boxShadow: "none",
                mb: 1,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0px 0px 8px rgba(65, 65, 110, 0.6)",
                },
              }}
            >
              <Avatar
                src={song.thumbnailImg?.filePath}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "6px",
                  mr: 1.5,
                  cursor: "pointer",
                }}
                onClick={() => handlePlaySong(song)}
              />
              <ListItemText
                primary={truncate(song.title, 15)}
                secondary={song.artistData?.[0]?.fullName || "Unknown Artist"}
                primaryTypographyProps={{ fontSize: "12px", fontWeight: "bold", color: "#ffffff" }}
                secondaryTypographyProps={{ color: "gray", fontSize: "10px" }}
                onClick={() => handlePlaySong(song)}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* TOP PLAYLISTS */}
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "12px", color: "#ffffff" }}>
            TOP PLAYLISTS
          </Typography>
          <Typography
            sx={{
              color: "#ffffff",
              fontSize: "9px",
              textTransform: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => history.push('/app/feature/MostStreamedThisWeekStudio')}
          >
            Explore More
          </Typography>
        </Box>
        <List>
          {topPlaylists.map((playlist) => (
            <ListItem
              key={playlist._id}
              sx={{
                pl: 1.5,
                py: 0.8,
                borderRadius: "8px",
                backgroundColor: "rgba(13, 13, 13, 0.9)",
                border: "1px solid transparent",
                background: `linear-gradient(black, black) padding-box, 
                             linear-gradient(135deg, #1f1f2e, #41416e) border-box`,
                boxShadow: "none",
                mb: 1,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0px 0px 8px rgba(65, 65, 110, 0.6)",
                },
              }}
            >
              <Avatar
                src={playlist.GIF?.filePath || playlist.thumbnailImg?.filePath || Music}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "6px",
                  mr: 1.5,
                  cursor: "pointer",
                }}
                onClick={() => history.push(`/app/Music/${playlist._id}`)}
              />
              <ListItemText
                primary={truncate(playlist.title, 15)}
                secondary={playlist.createdBy || "Unknown Artist"}
                primaryTypographyProps={{ fontSize: "12px", fontWeight: "bold", color: "#ffffff" }}
                secondaryTypographyProps={{ color: "gray", fontSize: "10px" }}
                onClick={() => history.push(`/app/Music/${playlist._id}`)}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* FEATURED ARTIST */}
      {featuredArtist && (
        <Box
          sx={{
            mt: 2,
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "rgba(13, 13, 13, 0.9)",
            border: "1px solid transparent",
            background: `linear-gradient(black, black) padding-box, 
                         linear-gradient(135deg, #1f1f2e, #41416e) border-box`,
            boxShadow: "none",
            transition: "transform 0.2s, box-shadow 0.2s",
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: "0px 0px 8px rgba(65, 65, 110, 0.6)",
            },
            cursor: "pointer",
          }}
          onClick={() => history.push(`/app/ArtistProfile/${featuredArtist._id}`)}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              p: 1,
              color: "#ffffff",
            }}
          >
            Featured Artist
          </Typography>
          <Box
            sx={{
              backgroundImage: `url(${featuredArtist.profileImg?.filePath})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              height: "180px",
            }}
          />
          <Typography
            variant="body1"
            sx={{
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
              py: 1,
              fontSize: "14px",
            }}
          >
            {featuredArtist.firstName} {featuredArtist.lastName}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SidebarRight;
